import React, { useEffect } from 'react';
import { Router } from 'react-router-dom';
import { connect } from 'react-redux';
import { GrowthBookProvider } from '@growthbook/growthbook-react';
import { GetLoggedInUser, InitApp } from 'actions';
import { IAppState } from 'types';
import history from './helpers/history';
import { useAuth0 } from './react-auth0-spa';
import usePolicyNotification from 'hooks/usePolicyNotification';
import LayoutContainer from 'containers/layout';
import useUserLoaded from 'hooks/useUserLoaded';
import useBundleNotification from 'hooks/useBundleNotification';
import useRenewalNotification from 'hooks/useRenewalNotification';
import AppBanner from 'components/app-banner';
import Loading from 'components/loading';
import { growthBook, growthBookInit } from './services/growthbook-service';
import { DefaultRoutes, OnboardingRoutes } from 'routes';
import { isOnboardingUser } from './helpers/user';
import { windowScrollTop } from './helpers/window';
import { PROFILE_ROUTE } from 'constants/routes';
import RootModalContainer from 'containers/root-modals';
import SessionExpiredContainer from 'containers/auth/SessionExpiredContainer';

interface IDispatchProps {
  initApp(): void;
  getLoggedInUser(): void;
}

const App = ({
  user,
  getLoggedInUser,
  initApp,
}: IAppState & IDispatchProps) => {
  const { authError, isAuthenticated, loginWithRedirect } = useAuth0();
  const isOnboarding = isOnboardingUser(user);

  useUserLoaded();
  useBundleNotification();
  usePolicyNotification();
  useRenewalNotification();

  useEffect(() => {
    if (isAuthenticated) {
      getLoggedInUser();
      initApp();
    } else {
      loginWithRedirect({ appState: { targetUrl: window.location.pathname } });
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (user.loaded) {
      growthBookInit(user);
    }
  }, [user.loaded]);

  useEffect(() => {
    // Scroll to top every route change except when we press back
    const unlisten = history.listen((locationState, action) => {
      if (action !== 'POP' && !locationState.pathname.includes(PROFILE_ROUTE)) {
        windowScrollTop();
      }
    });

    return () => {
      unlisten();
    };
  }, []);

  useEffect(() => {
    if (user.loaded && !isOnboarding) {
      // Remove confirmation popup once user is loaded
      // and onboarding is complete
      window.onbeforeunload = null;
    }
  }, [user.loaded, isOnboarding]);

  if (!isAuthenticated) {
    return authError ? <SessionExpiredContainer /> : null;
  }

  if (!user.loaded) {
    return <Loading full={true} />;
  }

  return (
    <Router history={history}>
      <GrowthBookProvider growthbook={growthBook}>
        {isOnboarding ? (
          <OnboardingRoutes />
        ) : (
          <>
            <RootModalContainer />
            <AppBanner />
            {isAuthenticated && (
              <LayoutContainer>
                <DefaultRoutes />
              </LayoutContainer>
            )}
          </>
        )}
      </GrowthBookProvider>
    </Router>
  );
};

const mapStateToProps = (state: IAppState) => ({
  ...state,
});

const mapDispatchToProps = {
  getLoggedInUser: GetLoggedInUser,
  initApp: InitApp,
};

export default connect<IAppState, IDispatchProps>(
  mapStateToProps,
  mapDispatchToProps,
)(App);
